import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Metadata, StartupManager } from '@memberjunction/core';

/**
 * Readiness gate for an app surface hosted inside MJExplorer.
 *
 * A standalone AGU app produced this signal itself: AppComponent configured the
 * GraphQL provider, constructed Metadata, then emitted `setupComplete$`, and
 * every data-touching component awaited it before making a call. Inside Explorer
 * all of that setup already happened — ng-workspace-initializer's
 * initializeWorkspace() runs setupGraphQLClient, refreshes metadata and
 * constructs Metadata before the shell routes anywhere — so re-running it here
 * would rebuild the provider underneath the rest of the app.
 *
 * The gate is kept rather than stubbed to `true`, for two reasons: the shell
 * renders on the `isAuthenticated()` emission rather than on an explicit
 * "workspace ready" flag, so a fast emission is theoretically able to mount a
 * dashboard mid-initialization; and keeping the contract means every migrated
 * component's `await this.waitForSetup()` line stays correct as written.
 *
 * StartupManager.Startup() is the right primitive to await: it is idempotent and
 * join-able, so this returns the same promise the shell's own startup is already
 * waiting on rather than starting a second one.
 *
 * Originally the Feedback Platform's FpReadyService; nothing in it was ever
 * FP-specific, and three more surfaces now need the same gate.
 */
@Injectable()
export class AGUSurfaceReadyService {
    private readonly setupCompleteSource = new BehaviorSubject<boolean>(false);
    private readonly setupErrorSource = new BehaviorSubject<string | null>(null);
    /** Deduplicates concurrent callers onto one in-flight initialization. */
    private setupPromise: Promise<void> | null = null;

    constructor(private readonly zone: NgZone) {}

    /** True once MJ metadata and the current user are known to be available. */
    get setupComplete$(): Observable<boolean> {
        return this.setupCompleteSource.asObservable();
    }

    /** Emits a message when readiness could not be established. */
    get setupError$(): Observable<string | null> {
        return this.setupErrorSource.asObservable();
    }

    get IsReady(): boolean {
        return this.setupCompleteSource.value;
    }

    /**
     * Resolves once the surface may safely make MJ data calls. Safe to call from
     * every component's ngOnInit; concurrent calls share one initialization.
     */
    async WaitForSetup(): Promise<void> {
        if (this.setupCompleteSource.value) {
            return;
        }
        this.setupPromise ??= this.Initialize();
        await this.setupPromise;
        // Await the emission itself so a caller that arrived mid-flight observes a
        // completed gate rather than racing the BehaviorSubject.
        await firstValueFrom(this.setupCompleteSource.pipe(filter((ready) => ready)));
    }

    private async Initialize(): Promise<void> {
        try {
            await StartupManager.Instance.Startup();
            const md = new Metadata();
            if (!md.CurrentUser) {
                throw new Error('No current user is available from MemberJunction metadata');
            }
            // Emit inside the zone: MJ's auth and GraphQL layers escape Angular's
            // zone patching, and a gate that resolves outside it leaves every
            // component's loading state stuck until some unrelated event ticks
            // change detection.
            this.zone.run(() => {
                this.setupErrorSource.next(null);
                this.setupCompleteSource.next(true);
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.zone.run(() => this.setupErrorSource.next(message));
            // Clear so a later attempt can retry rather than being permanently
            // wedged behind a failed first call.
            this.setupPromise = null;
            throw error;
        }
    }
}
